import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
  ReCaptchaV3Provider,
  type AppCheck,
} from "firebase/app-check";
import { getAuth, connectAuthEmulator } from "firebase/auth";
import { getFirestore, initializeFirestore, setLogLevel, connectFirestoreEmulator } from "firebase/firestore";
import { getStorage, connectStorageEmulator } from "firebase/storage";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";
import { FirebaseConfig } from "../types";
import { logger } from "../utils/logger";

// Debug environment variable loading (only in development and when explicitly enabled)
if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_FIREBASE === 'true') {
  logger.debug('🔍 Environment Variables Debug:', {
    NODE_ENV: process.env.NODE_ENV,
    hasApiKey: !!process.env.REACT_APP_FIREBASE_API_KEY,
    hasAuthDomain: !!process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    hasProjectId: !!process.env.REACT_APP_FIREBASE_PROJECT_ID,
    hasStorageBucket: !!process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    hasMessagingSenderId: !!process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    hasAppId: !!process.env.REACT_APP_FIREBASE_APP_ID,
    hasMeasurementId: !!process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
  });
}

// Validate required Firebase environment variables
const requiredEnvVars = [
  'REACT_APP_FIREBASE_API_KEY',
  'REACT_APP_FIREBASE_AUTH_DOMAIN', 
  'REACT_APP_FIREBASE_PROJECT_ID',
  'REACT_APP_FIREBASE_STORAGE_BUCKET',
  'REACT_APP_FIREBASE_MESSAGING_SENDER_ID',
  'REACT_APP_FIREBASE_APP_ID',
  'REACT_APP_FIREBASE_MEASUREMENT_ID'
];

const missingVars = requiredEnvVars.filter(varName => !process.env[varName]);
if (missingVars.length > 0) {
  logger.error('❌ Missing environment variables:', missingVars);
  logger.error('💡 Make sure your .env.local file exists and restart the dev server');
  throw new Error(`Missing required Firebase environment variables: ${missingVars.join(', ')}`);
}

// Central Firebase config - used by Firebase AI Logic for Gemini
const firebaseConfig: FirebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY!,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN!,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID!,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET!,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID!,
  appId: process.env.REACT_APP_FIREBASE_APP_ID!,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID!
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

function initFirebaseAppCheck(firebaseApp: FirebaseApp): AppCheck | null {
  if (typeof window === 'undefined' || process.env.NODE_ENV === 'test') {
    return null;
  }

  const enterpriseKey = process.env.REACT_APP_FIREBASE_APPCHECK_RECAPTCHA_ENTERPRISE_KEY?.trim();
  const v3SiteKey = (
    process.env.REACT_APP_FIREBASE_APPCHECK_RECAPTCHA_SITE_KEY ||
    process.env.REACT_APP_RECAPTCHA_SITE_KEY
  )?.trim();
  const providerPref = process.env.REACT_APP_FIREBASE_APPCHECK_PROVIDER?.trim().toLowerCase();

  if (!enterpriseKey && !v3SiteKey) {
    if (process.env.NODE_ENV === 'development') {
      logger.debug('[Firebase] App Check site key not set; skipping initializeAppCheck');
    }
    return null;
  }

  const hostname = window.location?.hostname?.trim().toLowerCase() || '';
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
  const debugTokenEnv = process.env.REACT_APP_FIREBASE_APPCHECK_DEBUG_TOKEN?.trim();

  if (process.env.NODE_ENV === 'development' && isLocalhost && debugTokenEnv && debugTokenEnv !== 'false') {
    (
      window as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean | string }
    ).FIREBASE_APPCHECK_DEBUG_TOKEN = debugTokenEnv === 'true' ? true : debugTokenEnv;
  }

  try {
    const useEnterprise = Boolean(enterpriseKey) || providerPref === 'enterprise';
    const siteKey = (useEnterprise ? enterpriseKey || v3SiteKey : v3SiteKey || enterpriseKey)!;
    const provider = useEnterprise
      ? new ReCaptchaEnterpriseProvider(siteKey)
      : new ReCaptchaV3Provider(siteKey);

    return initializeAppCheck(firebaseApp, {
      provider,
      isTokenAutoRefreshEnabled: true,
    });
  } catch (error) {
    logger.warn('[Firebase] App Check initialization warning:', error);
    return null;
  }
}

export const appCheck: AppCheck | null = initFirebaseAppCheck(app);

// Initialize Firebase services
export const auth = getAuth(app);
// Emulator WebChannel/fetch-streams often go "client is offline" in Chromium
// embedded browsers. Long polling is emulator-only; production stays default.
export const db =
  process.env.REACT_APP_USE_EMULATORS === 'true'
    ? initializeFirestore(app, { experimentalForceLongPolling: true })
    : getFirestore(app);

// Enable verbose Firestore logging in development if monitoring is enabled
if (process.env.NODE_ENV === 'development') {
  const isMonitoringEnabled = 
    typeof window !== 'undefined' && 
    localStorage.getItem('VOLUNTAS_FIRESTORE_MONITOR') === 'true';
  
  if (isMonitoringEnabled || process.env.REACT_APP_ENABLE_FIRESTORE_MONITOR === 'true') {
    setLogLevel('debug');
    logger.info('[Firebase] Verbose Firestore logging enabled for monitoring');
  }
}
// Force storage to use the correct bucket from env or default
export const storage = getStorage(app, process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || 'gs://voluntas-growth-engine-documents');

// DUAL-REGION SETUP: Chat functions in us-central1, all others in australia-southeast1
// Chat functions (Pro model access): chatWithAI, chatWithAIStreaming, enhancePromptManually, etc.
export const chatFunctions = getFunctions(app, 'us-central1');

// All other functions (investment, admin, etc.) remain in australia-southeast1
export const functions = getFunctions(app, 'australia-southeast1');

function isLocalEmulatorHostName(host: string): boolean {
  const normalized = host.trim().toLowerCase();
  return normalized === 'localhost' || normalized === '127.0.0.1';
}

function isFirebaseEmulatorHotReloadError(error: unknown): boolean {
  const err = error as { code?: string; message?: string };
  const blob = `${err?.code || ''} ${err?.message || ''}`.toLowerCase();
  return (
    blob.includes('already been called') ||
    blob.includes('already been initialized') ||
    blob.includes('already been started') ||
    blob.includes('already connected')
  );
}

function hostPortLooksLocal(hostPort: string, expectedPort: number): boolean {
  const normalized = hostPort.trim().toLowerCase().replace(/^https?:\/\//, '');
  return (
    normalized === `localhost:${expectedPort}` ||
    normalized === `127.0.0.1:${expectedPort}`
  );
}

function authAlreadyOnLocalEmulator(): boolean {
  const cfg = auth.emulatorConfig;
  if (!cfg) return false;
  return isLocalEmulatorHostName(String(cfg.host || '')) && Number(cfg.port) === 9099;
}

function firestoreAlreadyOnLocalEmulator(): boolean {
  const settings = (db as { _settings?: { host?: string } })._settings;
  return hostPortLooksLocal(String(settings?.host || ''), 8080);
}

function storageAlreadyOnLocalEmulator(): boolean {
  const storageAny = storage as { _host?: string; host?: string; _port?: number; port?: number };
  const host = String(storageAny._host || storageAny.host || '');
  const port = Number(storageAny._port ?? storageAny.port);
  return isLocalEmulatorHostName(host) && port === 9199;
}

function functionsAlreadyOnLocalEmulator(instance: typeof functions): boolean {
  const fnAny = instance as { emulatorOrigin?: string; _url?: string };
  const origin = String(fnAny.emulatorOrigin || fnAny._url || '');
  return hostPortLooksLocal(origin, 5001);
}

function connectEmulatorFailClosed(
  label: string,
  connect: () => void,
  isAlreadyLocal: () => boolean
): void {
  try {
    connect();
  } catch (error) {
    if (isAlreadyLocal() || isFirebaseEmulatorHotReloadError(error)) {
      logger.debug(`[Firebase] ${label} emulator already connected (hot reload)`);
      return;
    }
    logger.error(
      `[Firebase] Failed to connect ${label} emulator — refusing production fallback`,
      error
    );
    throw error instanceof Error
      ? error
      : new Error(`[Firebase] Failed to connect ${label} emulator`);
  }
}

// Fail closed whenever the emulator flag is on: never fall through to production.
if (process.env.REACT_APP_USE_EMULATORS === 'true') {
  if (process.env.NODE_ENV !== 'development') {
    throw new Error(
      'REACT_APP_USE_EMULATORS is true outside development — refusing production Firebase'
    );
  }
  connectEmulatorFailClosed(
    'Auth',
    () => connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true }),
    authAlreadyOnLocalEmulator
  );
  connectEmulatorFailClosed(
    'Firestore',
    () => connectFirestoreEmulator(db, 'localhost', 8080),
    firestoreAlreadyOnLocalEmulator
  );
  connectEmulatorFailClosed(
    'Storage',
    () => connectStorageEmulator(storage, 'localhost', 9199),
    storageAlreadyOnLocalEmulator
  );
  connectEmulatorFailClosed(
    'Functions',
    () => connectFunctionsEmulator(functions, 'localhost', 5001),
    () => functionsAlreadyOnLocalEmulator(functions)
  );
  connectEmulatorFailClosed(
    'Chat Functions',
    () => connectFunctionsEmulator(chatFunctions, 'localhost', 5001),
    () => functionsAlreadyOnLocalEmulator(chatFunctions)
  );
}

export default app; 